import type { Theme } from "@mui/material";
import { Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import {
	ClientPortfolioDomainEnum,
	type CurrencyEnum,
	type PortfolioListItemResponseDTO,
	type PortfolioListReportingSummaryResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	type useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PercentageDelta } from "web_myplann/Shared/Components/PercentageDelta";
import {
	dateFormatter,
	moneyFormatter,
} from "web_myplann/Shared/Utils/formatters";
import { roundNumber } from "web_myplann/Utils/formatters";

const INSURANCE_DOMAINS = new Set([
	ClientPortfolioDomainEnum.LifeInsurance,
	ClientPortfolioDomainEnum.PropertyInsurance,
]);

const INVESTMENT_DOMAINS = new Set([
	ClientPortfolioDomainEnum.PensionProducts,
	ClientPortfolioDomainEnum.BuildingSavings,
	ClientPortfolioDomainEnum.Investments,
]);

const LOAN_DOMAINS = new Set([
	ClientPortfolioDomainEnum.BusinessLoans,
	ClientPortfolioDomainEnum.Loans,
	ClientPortfolioDomainEnum.Leasing,
]);

const ASSET_DOMAINS = new Set([
	ClientPortfolioDomainEnum.Commodities,
	ClientPortfolioDomainEnum.BankingProducts,
	ClientPortfolioDomainEnum.Cryptocurrencies,
]);

const SERVICE_DOMAINS = new Set([
	ClientPortfolioDomainEnum.Services,
	ClientPortfolioDomainEnum.PaidAdvisory,
]);

type TranslateFunction = ReturnType<typeof useResource>["t"];

function normalizeChange(change: number | null | undefined): number | null {
	if (change == null) {
		return null;
	}
	const rounded = roundNumber(change);
	return rounded <= 0.01 && rounded >= -0.01 ? 0 : rounded;
}

function getCurrencySign(t: TranslateFunction, currency: CurrencyEnum): string {
	return (
		t(Resources.PortfolioDetail.SharedLabels.Currency[currency]) ??
		t(Resources.Common.CurrencySign)
	);
}

function renderInsuranceRow(
	data: PortfolioListItemResponseDTO,
	t: TranslateFunction,
	theme: Theme,
): ReactNode {
	let dateRange = "";
	if (data.startAt) {
		const endDate = data.endAt
			? dateFormatter(data.endAt)
			: t(Resources.Common.NotSpecified);
		dateRange = `${dateFormatter(data.startAt)} - ${endDate}`;
	}

	return (
		<Typography fontSize={14} fontWeight={400} color={theme.palette.grey[500]}>
			{dateRange}
		</Typography>
	);
}

function renderValueWithDelta(
	currentValue: number,
	change: number | null,
	currencySign: string,
	theme: Theme,
): ReactNode {
	return (
		<Stack direction="row" gap={0.8}>
			<Typography
				fontSize={14}
				fontWeight={400}
				color={theme.palette.grey[500]}
			>
				{moneyFormatter(currentValue, currencySign)}
			</Typography>
			{currentValue !== 0 && <PercentageDelta change={change} />}
		</Stack>
	);
}

function renderValueOnly(
	currentValue: number,
	currencySign: string,
	theme: Theme,
): ReactNode {
	return (
		<Typography fontSize={14} fontWeight={400} color={theme.palette.grey[500]}>
			{moneyFormatter(currentValue, currencySign)}
		</Typography>
	);
}

function renderServiceRow(
	data: PortfolioListItemResponseDTO,
	theme: Theme,
): ReactNode {
	return (
		<Stack direction="row" gap={0.8}>
			<Typography
				fontSize={14}
				fontWeight={400}
				color={theme.palette.grey[500]}
				sx={{
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
					maxWidth: "100%",
				}}
			>
				{data.contractNumber}
			</Typography>
		</Stack>
	);
}

export function renderThirdRow(
	data: PortfolioListItemResponseDTO,
	summaryData: PortfolioListReportingSummaryResponseDTO[] | undefined,
	t: TranslateFunction,
	theme: Theme,
): ReactNode {
	const domain = data.domain;
	if (domain == null) {
		return null;
	}

	if (INSURANCE_DOMAINS.has(domain)) {
		return renderInsuranceRow(data, t, theme);
	}

	if (SERVICE_DOMAINS.has(domain)) {
		return renderServiceRow(data, theme);
	}

	const item = summaryData?.find((item) => item.id === data.id);
	if (!item?.summary) {
		return null;
	}

	const currentValue = roundNumber(item.summary.totalValue ?? 0, 0);
	const currencySign = getCurrencySign(t, item.summary.currency);

	if (INVESTMENT_DOMAINS.has(domain)) {
		const change = normalizeChange(item.summary.totalEvaluationPercent);
		return renderValueWithDelta(currentValue, change, currencySign, theme);
	}

	if (LOAN_DOMAINS.has(domain)) {
		return renderValueOnly(currentValue, currencySign, theme);
	}

	if (ASSET_DOMAINS.has(domain)) {
		const change = normalizeChange(item.summary.totalEvaluationPercent);
		return renderValueWithDelta(currentValue, change, currencySign, theme);
	}

	return null;
}

export function renderReportingThirdRow(
	data: PortfolioListItemResponseDTO,
	summaryData: PortfolioListReportingSummaryResponseDTO[] | undefined,
	t: TranslateFunction,
	theme: Theme,
): ReactNode {
	const item = summaryData?.find((item) => item.id === data.id);
	if (!item?.summary) {
		return null;
	}

	const currentValue = roundNumber(item.summary.totalValue ?? 0, 0);
	const currencySign = getCurrencySign(t, item.summary.currency);
	const change = normalizeChange(item.summary.totalEvaluationPercent);
	return renderValueWithDelta(currentValue, change, currencySign, theme);
}

export function getTitle(
	data: PortfolioListItemResponseDTO,
	t: TranslateFunction,
): string {
	const title = data.name ?? data.product;
	if (title) {
		return title;
	}
	return data.domain != null
		? t(Resources.Portfolio.List.PortfolioDomainType[data.domain])
		: "";
}
